import React, { useState, useMemo } from 'react';
import { Search, MessageSquare, CheckCircle, Clock, XCircle, Eye, Archive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { useAdminData } from './AdminContext';
import { formatDateTime } from './utils';

const AdminFeedbackTab = () => {
  const { feedback, selectedFeedbackFilter, setSelectedFeedbackFilter, updateFeedbackStatus } = useAdminData();
  
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [resolvingFeedback, setResolvingFeedback] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('resolved');

  const filteredFeedback = useMemo(() => {
    return feedback.filter(item => {
      if (feedbackSearch === '') return true;
      const searchLower = feedbackSearch.toLowerCase();
      return (
        item.user_name?.toLowerCase().includes(searchLower) ||
        item.subject?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.user_email?.toLowerCase().includes(searchLower)
      );
    });
  }, [feedback, feedbackSearch]);

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'bug': return '🐛 Bug';
      case 'feature_request': return '✨ Feature';
      case 'confusing': return '🤔 Confusing';
      case 'report': return '⚠️ Report';
      default: return '💬 Other';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return { variant: 'destructive', class: 'bg-blue-100 text-blue-800', label: 'Submitted' };
      case 'in_review': return { variant: 'default', class: 'bg-amber-100 text-amber-800', label: 'In Review' };
      case 'resolved': return { variant: 'success', class: 'bg-green-100 text-green-800', label: 'Resolved' };
      case 'closed': return { variant: 'secondary', class: 'bg-gray-100 text-gray-800', label: 'Closed' };
      default: return { variant: 'secondary', class: 'bg-gray-100 text-gray-800', label: status };
    }
  };

  const handleUpdateStatus = async () => {
    await updateFeedbackStatus(resolvingFeedback.id, selectedStatus, adminComment);
    setResolvingFeedback(null);
    setAdminComment('');
    setSelectedStatus('resolved');
  };

  const handleQuickStatusChange = async (feedbackItem, newStatus) => {
    await updateFeedbackStatus(feedbackItem.id, newStatus);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Customer Feedback</CardTitle>
          <CardDescription>View and manage customer feedback submissions</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by customer, subject, or message..."
                value={feedbackSearch}
                onChange={(e) => setFeedbackSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedFeedbackFilter} onValueChange={setSelectedFeedbackFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Feedback</SelectItem>
                <SelectItem value="new">Submitted</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Feedback Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Page URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFeedback.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {feedbackSearch ? 'No feedback matches your search' : 'No feedback submissions found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFeedback.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="text-sm">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-medium">{item.user_name}</p>
                            {item.user_role && (
                              <Badge 
                                variant="outline" 
                                className={item.user_role === 'vendor' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}
                              >
                                {item.user_role === 'vendor' ? 'Vendor' : 'Customer'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-500">{item.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getCategoryBadge(item.category)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-sm truncate">{item.subject}</p>
                          <p className="text-xs text-gray-500 truncate">{item.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.page_url && (
                          <a 
                            href={item.page_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline truncate block max-w-xs"
                          >
                            {new URL(item.page_url).pathname}
                          </a>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(item.status).class}>
                          {getStatusBadge(item.status).label}
                        </Badge>
                        {item.admin_comment && (
                          <p className="text-xs text-gray-500 mt-1 truncate max-w-[100px]" title={item.admin_comment}>
                            💬 Has note
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(item.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {item.status === 'new' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleQuickStatusChange(item, 'in_review')}
                              title="Mark as In Review"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          )}
                          {(item.status === 'new' || item.status === 'in_review') && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setResolvingFeedback(item);
                                setAdminComment(item.admin_comment || '');
                                setSelectedStatus('resolved');
                              }}
                              title="Resolve with note"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Resolve
                            </Button>
                          )}
                          {item.status === 'resolved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleQuickStatusChange(item, 'closed')}
                              title="Close this feedback"
                            >
                              <Archive className="h-4 w-4 mr-1" />
                              Close
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resolution Dialog */}
      {resolvingFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Update Feedback Status</CardTitle>
              <CardDescription>Add a resolution note that the user will be able to see</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded space-y-2">
                <p className="font-medium">{resolvingFeedback.subject}</p>
                <p className="text-sm text-gray-600">{resolvingFeedback.description}</p>
                <p className="text-xs text-gray-500">From: {resolvingFeedback.user_name} ({resolvingFeedback.user_email})</p>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_review">In Review</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resolution Note (Visible to User)</Label>
                <Textarea
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  placeholder="Add a note explaining how this was addressed or any response for the user..."
                  rows={3}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This note will be visible to the user in their feedback detail view.
                </p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleUpdateStatus} className="flex-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Update Status
                </Button>
                <Button variant="outline" onClick={() => { setResolvingFeedback(null); setAdminComment(''); setSelectedStatus('resolved'); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default AdminFeedbackTab;
